import React from "react";
import { useNavigate } from "react-router-dom";
import { FiCheckCircle } from "react-icons/fi";

export default function AboutSection() {
  const navigate = useNavigate();

  return (
  <section className="relative overflow-hidden bg-[#0A0A0A]">
    {/* 🌊 Top Wave Transition */}
    <svg
      className="absolute top-0 left-0 w-full z-0"
      viewBox="0 0 1440 320"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill="#0A0A0A"
        fillOpacity="1"
        d="M0,160L60,144C120,128,240,96,360,101.3C480,107,600,149,720,165.3C840,181,960,171,1080,149.3C1200,128,1320,96,1380,80L1440,64L1440,0L0,0Z"
      ></path>
    </svg>

    {/* ✨ GOLD LUXURY BACKGROUND */}
    <div className="absolute inset-0 z-0 bg-gradient-to-b from-black via-[#0F0F0F] to-black">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(212,175,55,0.25),_transparent_60%)]"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_rgba(212,175,55,0.20),_transparent_70%)]"></div>
      <div className="absolute inset-0 bg-gradient-to-r from-black via-[#1A1A1A]/40 to-black opacity-60 animate-gradientFlow"></div>
    </div>

    {/* Gold Glow Orbs */}
    <div className="absolute -z-10 w-[900px] h-[900px] bg-[#D4AF37] rounded-full blur-[220px] opacity-15 top-1/2 left-[-200px] animate-float-smooth"></div>
    <div className="absolute -z-10 w-[700px] h-[700px] bg-[#F5D87A] rounded-full blur-[200px] opacity-10 bottom-0 right-[-200px] animate-float-smooth"></div>

    {/* ✨ Gold Particles */}
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(15)].map((_, i) => (
        <div
          key={i}
          className="absolute bg-[#D4AF37] rounded-full opacity-40 blur-[2px]"
          style={{
            width: `${Math.random() * 6 + 2}px`,
            height: `${Math.random() * 6 + 2}px`,
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            animation: `floatParticle ${6 + Math.random() * 6}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 5}s`,
          }}
        ></div>
      ))}
    </div>

    <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-16 py-28 grid md:grid-cols-2 gap-16 items-center">
      {/* LEFT SIDE */}
      <div data-aos="fade-right" data-aos-duration="800">
        <h5 className="text-sm font-semibold text-[#D4AF37] mb-2 tracking-wide uppercase">
          About Us
        </h5>

        <h2 className="text-4xl font-bold text-white mb-4 leading-tight">
          Crafting Premium Digital Experiences
          <br /> That Inspire & Empower
        </h2>

        <p className="text-gray-300 mb-6 max-w-lg text-lg leading-relaxed">
          We are a luxury-focused digital agency helping brands elevate their presence with
          elite design, modern development, and flawless execution.
        </p>

        {/* GOLD LIST */}
        <div className="space-y-3 mb-8">
          {[
            "Premium User-Centric Design",
            "High-End Seamless Development",
            "Long-Term Reliable Support",
          ].map((point, i) => (
            <div
              key={i}
              className="flex items-center gap-3 bg-white/10 backdrop-blur-sm p-3 rounded-full w-fit shadow-sm hover:shadow-md transition-all duration-300"
            >
              <FiCheckCircle className="text-[#D4AF37] text-xl" />
              <span className="text-gray-200 font-medium">{point}</span>
            </div>
          ))}
        </div>

        {/* GOLD BUTTON */}
        <button
          onClick={() => navigate("/contact")}
          className="inline-flex items-center justify-center px-6 py-3 text-sm sm:text-base font-semibold text-black bg-[#D4AF37] hover:bg-[#F1D488] rounded-full shadow-md hover:shadow-lg transition-all duration-300"
        >
          Get In Touch
          <svg
            className="w-5 h-5 ml-2 transition-transform duration-300 group-hover:translate-x-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>

      {/* RIGHT IMAGE */}
      <div
        className="relative flex justify-center items-center"
        data-aos="fade-left"
        data-aos-duration="800"
      >
        <div className="absolute -inset-3 bg-gradient-to-r from-[#D4AF37] to-[#F1D488] opacity-20 blur-xl rounded-3xl transition-all duration-700"></div>

        <img
          src="https://images.presentationgo.com/2025/04/team-collaboration-modern-office.jpg"
          alt="Team collaborating"
          className="relative w-full max-w-[700px] rounded-3xl object-cover shadow-2xl transition-transform duration-700 ease-in-out hover:scale-[1.03] hover:shadow-[#D4AF37]/40"
          loading="lazy"
          decoding="async"
        />
      </div>
    </div>

    {/* Animations */}
    <style>{`
      @keyframes floatSmooth {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-8px); }
      }
      .animate-float-smooth {
        animation: floatSmooth 6s ease-in-out infinite;
      }

      @keyframes gradientFlow {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
      .animate-gradientFlow {
        background-size: 200% 200%;
        animation: gradientFlow 12s ease infinite;
      }

      @keyframes floatParticle {
        0%, 100% { transform: translateY(0px); opacity: 0.6; }
        50% { transform: translateY(-20px); opacity: 1; }
      }
    `}</style>
  </section>
);

}
